import { Divider, Form, Tag } from 'antd';
import AppTypography from '../../typography/Typography';
import { FIELD_REGISTRY, getFieldFormItemProps } from './fieldRegistry.jsx';
import { resolveOptions } from './utils/popupUtils';
import { securityValidationRule } from '../../form/inputSecurity';

// Popup field types that accept free text — these get the same central
// input-security rule (script/HTML/SQL/command-injection blocking) the
// AddFormV1/EditFormV1 engines apply to every form field. New popup
// definitions inherit it automatically.
const TEXT_LIKE_POPUP_TYPES = new Set(['input', 'text', 'textarea', 'mention-textarea']);

/**
 * Renders the form section of DynamicPopup, including:
 *   - The "Already Assigned People" tag list (for others-assign)
 *   - All form fields via FIELD_REGISTRY
 *
 * @param {Object} props
 * @param {Object} props.form - Ant Design form instance
 * @param {Array} props.fields
 * @param {Object} props.initialValues
 * @param {Object} props.dynamicOptions
 * @param {Object} props.popupContext
 * @param {*} props.extraData
 * @param {string[]} props.removedIds
 * @param {Function} props.setRemovedIds
 * @param {boolean} [props.showAlreadyAssigned]
 * @param {Function} props.onValuesChange
 * @param {Function} props.getSearchHandler
 * @param {Function} props.getMentionSearchHandler
 * @param {Object} props.mentionOptions
 */
export default function PopupFields({
  form,
  fields,
  initialValues,
  dynamicOptions,
  popupContext,
  extraData,
  removedIds,
  setRemovedIds,
  showAlreadyAssigned,
  onValuesChange,
  getSearchHandler,
  getMentionSearchHandler,
  mentionOptions,
}) {
  const assignedPeople = (extraData?.recruitersData ?? []).filter((recruiter) => {
    const id = String(
      recruiter?.user_id ?? recruiter?.id ?? recruiter?.userId ?? '',
    );
    return id && !removedIds.includes(id);
  });
  const showAssignedSection = showAlreadyAssigned && extraData;

  // Someone already on the job must not be offered for assignment again —
  // picking them was a no-op at best, and the popup looked broken because the
  // same person sat in "Already Assigned" AND in the dropdown.
  //
  // Derived from `assignedPeople`, which is already "assigned AND not pending
  // removal". That is what makes the reverse work for free: un-assign someone
  // with the tag's x and they become selectable again immediately, which is
  // exactly what a user who removed them by mistake expects.
  const assignedOptionIds = new Set(
    showAlreadyAssigned
      ? assignedPeople
        .map((r) => String(r?.user_id ?? r?.id ?? r?.userId ?? '').trim())
        .filter(Boolean)
      : [],
  );
  const withoutAssigned = (options) => (
    assignedOptionIds.size === 0 || !Array.isArray(options)
      ? options
      : options.filter((opt) => !assignedOptionIds.has(
        String((opt && typeof opt === 'object' ? (opt.value ?? opt.id) : opt) ?? '').trim(),
      ))
  );

  if (!fields.length && !showAssignedSection) return null;

  return (
    <>
      {showAssignedSection && (
        <>
          <div className="dynamic-popup__already-assigned">
            <AppTypography variant="caption" color="secondary">Already Assigned People</AppTypography>
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {assignedPeople.length === 0 ? (
                <AppTypography variant="caption" color="secondary">
                  No one is currently assigned to this job.
                </AppTypography>
              ) : assignedPeople.map((recruiter) => {
                  const id = String(
                    recruiter?.user_id ?? recruiter?.id ?? recruiter?.userId ?? '',
                  );
                  const name =
                    recruiter?.name ??
                    recruiter?.user_name ??
                    recruiter?.userName ??
                    recruiter?.email ??
                    'Assigned user';
                  return (
                    <Tag
                      key={id || name}
                      closable
                      onClose={() => {
                        if (!id) return;
                        setRemovedIds((prev) => Array.from(new Set([...prev, id])));
                      }}
                    >
                      {name}
                    </Tag>
                  );
                })}
            </div>
          </div>
          <Divider style={{ margin: '12px 0' }} />
        </>
      )}

      {fields.length > 0 && (
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onValuesChange={(_, allValues) => onValuesChange(allValues)}
        >
          {groupFieldsIntoRows(fields).map((rowFields) => renderRow(rowFields, (field) => {
            const options = withoutAssigned(dynamicOptions[field.name] ?? resolveOptions(field, popupContext));
            const onSearch = getSearchHandler(field);
            const onMentionSearch = getMentionSearchHandler(field);
            const currentMentionOpts = mentionOptions[field.name] ?? [];
            const formItemProps = getFieldFormItemProps(field.type);
            const Control = FIELD_REGISTRY[field.type];

            const rules = [];
            if (field.required) {
              rules.push({ required: true, message: `${field.label || 'This field'} is required` });
            }
            if (typeof field.validator === 'function') {
              rules.push({
                validator: (_, value) => {
                  const errorMessage = field.validator(value, popupContext);
                  return errorMessage ? Promise.reject(new Error(errorMessage)) : Promise.resolve();
                },
              });
            }
            if (TEXT_LIKE_POPUP_TYPES.has(field.type)) {
              // maxLength is forwarded so the submit-time length check honours the
              // field's own cap instead of the generic 5000 default.
              rules.push(securityValidationRule({
                label: field.label, field: field.name, type: field.type, maxLength: field.maxLength,
              }));
            }

            return (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={rules}
                {...formItemProps}
              >
                {Control ? (
                  <Control
                    field={field}
                    options={options}
                    onSearch={onSearch}
                    mentionOpts={currentMentionOpts}
                    onMentionSearch={onMentionSearch}
                    popupContext={popupContext}
                  />
                ) : null}
              </Form.Item>
            );
          }))}
        </Form>
      )}
    </>
  );
}

// ── Inline rows ───────────────────────────────────────────────────────────────
// Consecutive fields sharing the same `inlineRow` key render side-by-side on
// one line (e.g. the Approve Rate popup's Proposed Rate + Currency + Unit).
// Fields without inlineRow keep the default one-per-line layout, so every
// existing popup is unaffected. Generic — any popup definition can opt in.
function groupFieldsIntoRows(fields) {
  const rows = [];
  for (const field of fields) {
    const previous = rows[rows.length - 1];
    if (field.inlineRow && previous?.inlineRow === field.inlineRow) {
      previous.fields.push(field);
    } else {
      rows.push({ inlineRow: field.inlineRow, fields: [field] });
    }
  }
  return rows.map((row) => row.fields);
}

function renderRow(rowFields, renderField) {
  if (rowFields.length === 1) return renderField(rowFields[0]);
  return (
    <div
      key={`row-${rowFields.map((f) => f.name).join('-')}`}
      style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}
    >
      {rowFields.map((field, index) => (
        <div key={field.name} style={{ flex: index === 0 ? 2 : 1, minWidth: 0 }}>
          {renderField(field)}
        </div>
      ))}
    </div>
  );
}
